import { useMemo } from 'react'

type TimestampFormat = 'datetime' | 'date' | 'time' | 'relative'

interface TimestampProps {
  value: string | undefined | null
  format?: TimestampFormat
  fallback?: string
  className?: string
}

const SHORT_TZ_OPTIONS: Intl.DateTimeFormatOptions = { timeZoneName: 'short' }

function getTimezoneAbbr(): string {
  try {
    const parts = new Intl.DateTimeFormat(undefined, SHORT_TZ_OPTIONS).formatToParts(new Date())
    return parts.find(p => p.type === 'timeZoneName')?.value ?? ''
  } catch {
    return ''
  }
}

function formatUTC(date: Date): string {
  return date.toISOString().replace('T', ' ').replace(/\.\d+Z$/, ' UTC')
}

function formatRelative(date: Date): string {
  const now = Date.now()
  const diff = now - date.getTime()
  const isFuture = diff < 0
  const seconds = Math.floor(Math.abs(diff) / 1000)
  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return isFuture ? `in ${minutes}m` : `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return isFuture ? `in ${hours}h` : `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 30) return isFuture ? `in ${days}d` : `${days}d ago`
  const months = Math.floor(days / 30)
  if (months < 12) return isFuture ? `in ${months}mo` : `${months}mo ago`
  const years = Math.floor(months / 12)
  return isFuture ? `in ${years}y` : `${years}y ago`
}

/**
 * Timezone-aware timestamp component.
 * Shows local time with timezone abbreviation; hovering reveals UTC time.
 */
export function Timestamp({ value, format = 'datetime', fallback = '-', className }: TimestampProps) {
  const { display, utc, tzAbbr } = useMemo(() => {
    if (!value) return { display: '', utc: '', tzAbbr: '' }
    // A Go zero-value time.Time (a field that was never set, serialized
    // without a pointer + `omitempty`) marshals to "0001-01-01T00:00:00Z" —
    // a non-empty, parseable string, so `!value` and `isNaN(date.getTime())`
    // both pass it through. Rendered as `relative`, that reads as "2025y
    // ago": a "never happened" sentinel presented as a measured duration,
    // the same class of bug as the hardcoded 9999-second snapshot-age
    // sentinel fixed in heartbeat_metrics.go (see EngineRolloutPanel.tsx's
    // formatAge, which carries the same guard). Treat it as absent.
    if (value.startsWith('0001-')) return { display: '', utc: '', tzAbbr: '' }
    const date = new Date(value)
    if (isNaN(date.getTime())) return { display: '', utc: '', tzAbbr: '' }

    const tzAbbr = getTimezoneAbbr()
    const utc = formatUTC(date)

    let display: string
    switch (format) {
      case 'date':
        display = date.toLocaleDateString(undefined, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        })
        break
      case 'time':
        display = date.toLocaleTimeString(undefined, {
          hour: '2-digit',
          minute: '2-digit',
        })
        break
      case 'relative':
        display = formatRelative(date)
        break
      case 'datetime':
      default:
        display = date.toLocaleString(undefined, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
        break
    }

    return { display, utc, tzAbbr }
  }, [value, format])

  if (!display) return <span className={className}>{fallback}</span>

  return (
    <time
      dateTime={value!}
      title={utc}
      className={className}
    >
      {display}
      {format !== 'relative' && tzAbbr && (
        <span className="ml-1 text-gray-400 text-[0.85em]">{tzAbbr}</span>
      )}
    </time>
  )
}
